import { CartLinesUpdateEvent } from '@shopify/events';

const dialog = document.getElementById('labhya-quick-view-dialog');
const content = dialog?.querySelector('[data-labhya-qv-content]');
const loading = dialog?.querySelector('[data-labhya-qv-loading]');
const errorBox = dialog?.querySelector('[data-labhya-qv-error]');
let activeProduct = null;
let activeImages = [];
let activeImageIndex = 0;

function money(cents) {
  const currency = window.Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
  } catch (_) {
    return `Rs. ${((Number(cents) || 0) / 100).toFixed(2)}`;
  }
}

function productJsonUrl(productUrl) {
  const u = new URL(productUrl, window.location.origin);
  u.pathname = `${u.pathname.replace(/\/$/, '')}.js`;
  return u.toString();
}

function escapeHtml(value = '') {
  return String(value).replace(/[&<>'"]/g, (ch) => ({ '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;' }[ch]));
}

function textFromHtml(html = '') {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
}

function rawImage(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.src || value.url || '';
}

function cleanImageKey(value) {
  const src = rawImage(value);
  if (!src) return '';
  try {
    const u = new URL(src, window.location.origin);
    return `${u.origin}${u.pathname}`.replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|\d+x\d*|x\d+)(?=\.[a-z]+$)/i, '');
  } catch (_) {
    return src.split('?')[0];
  }
}

function variantImage(variant, product) {
  return rawImage(variant?.featured_image) || rawImage(product?.featured_image) || rawImage(product?.images?.[0]) || '';
}

function productImages(product) {
  const candidates = [];
  if (Array.isArray(product?.images)) candidates.push(...product.images);
  if (product?.featured_image) candidates.unshift(product.featured_image);
  for (const variant of product?.variants || []) {
    const image = variantImage(variant, product);
    if (image) candidates.push(image);
  }
  const seen = new Set();
  return candidates.map(rawImage).filter(Boolean).filter((src) => {
    const key = cleanImageKey(src);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function galleryMarkup(product, firstAvailable) {
  activeImages = productImages(product);
  const initial = variantImage(firstAvailable, product) || activeImages[0] || '';
  const initialKey = cleanImageKey(initial);
  const found = activeImages.findIndex((src) => cleanImageKey(src) === initialKey);
  activeImageIndex = found >= 0 ? found : 0;
  if (!activeImages.length && initial) activeImages = [initial];

  const hasMany = activeImages.length > 1;
  const thumbs = activeImages.map((src, index) => `
    <button type="button" class="labhya-qv-thumb" data-labhya-qv-thumb="${index}" aria-label="View image ${index + 1}" aria-current="${index === activeImageIndex ? 'true' : 'false'}">
      <img src="${escapeHtml(src)}" alt="${escapeHtml(product.title)} — image ${index + 1}" loading="lazy">
    </button>`).join('');

  return `
    <div class="labhya-qv-stage">
      <img data-labhya-qv-image src="${escapeHtml(activeImages[activeImageIndex] || initial)}" alt="${escapeHtml(product.title)}">
      ${hasMany ? `<div class="labhya-qv-gallery-nav" aria-label="Product images">
        <button type="button" data-labhya-qv-prev aria-label="Previous image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <button type="button" data-labhya-qv-next aria-label="Next image"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
      </div>` : ''}
      ${activeImages.length ? `<span class="labhya-qv-count" data-labhya-qv-count>${activeImageIndex + 1} / ${activeImages.length}</span>` : ''}
    </div>
    ${hasMany ? `<div class="labhya-qv-thumbs" data-labhya-qv-thumbs>${thumbs}</div>` : ''}`;
}

function sizeGuideKind(product) {
  const type = String(product?.type || '').toLowerCase();
  const title = String(product?.title || '').toLowerCase();
  const text = `${type} ${title}`;
  if (text.includes('saree')) {
    if (text.includes('ready') || text.includes('pre-stitched') || text.includes('pre stitched') || text.includes('stitched saree')) return 'rtw-saree';
    return 'saree';
  }
  if (text.includes('co-ord') || text.includes('coord') || text.includes('kurta') || text.includes('kurti')) return 'ethnicwear';
  return '';
}

function sizeGuideMarkup(product) {
  const kind = sizeGuideKind(product);
  if (!kind) return '';

  let heading = 'Size guide';
  let note = 'Reference body measurements in inches. If the product page lists its own measurements, those take priority.';
  let table = '';

  if (kind === 'ethnicwear') {
    heading = /co-ord|coord/i.test(`${product?.type || ''} ${product?.title || ''}`) ? 'Co-ord set size guide' : 'Kurti & kurta size guide';
    table = `<div class="labhya-qv-size-scroll"><table class="labhya-qv-size-table"><thead><tr><th>Size</th><th>Bust</th><th>Waist</th><th>Hip</th></tr></thead><tbody>
      <tr><th>XS</th><td>34</td><td>30</td><td>36</td></tr><tr><th>S</th><td>36</td><td>32</td><td>38</td></tr><tr><th>M</th><td>38</td><td>34</td><td>40</td></tr><tr><th>L</th><td>40</td><td>36</td><td>42</td></tr><tr><th>XL</th><td>42</td><td>38</td><td>44</td></tr><tr><th>2XL</th><td>44</td><td>40</td><td>46</td></tr><tr><th>3XL</th><td>46</td><td>42</td><td>48</td></tr>
    </tbody></table></div>`;
  } else if (kind === 'rtw-saree') {
    heading = 'Ready-to-wear saree size guide';
    note = 'Use your natural waist as the main starting point. Blouse sizing, when included, follows the product-specific measurements.';
    table = `<div class="labhya-qv-size-scroll"><table class="labhya-qv-size-table"><thead><tr><th>Size</th><th>Waist</th></tr></thead><tbody>
      <tr><th>XS</th><td>26–28</td></tr><tr><th>S</th><td>28–30</td></tr><tr><th>M</th><td>30–32</td></tr><tr><th>L</th><td>32–34</td></tr><tr><th>XL</th><td>34–36</td></tr><tr><th>2XL</th><td>36–38</td></tr><tr><th>3XL</th><td>38–40</td></tr>
    </tbody></table></div>`;
  } else {
    heading = 'Normal saree fit guide';
    note = 'Normal sarees are generally free-size. Check the individual product page for saree length, width, blouse-piece inclusion and any stitched components.';
    table = `<div class="labhya-qv-size-scroll"><table class="labhya-qv-size-table"><thead><tr><th>Item</th><th>Typical reference</th></tr></thead><tbody><tr><th>Saree</th><td>Approx. 5.5 m length</td></tr><tr><th>Width</th><td>Approx. 44–47 in</td></tr><tr><th>Blouse piece</th><td>Approx. 0.8 m when included</td></tr></tbody></table></div>`;
  }

  return `<div class="labhya-qv-tools"><button type="button" class="labhya-qv-size-toggle" data-labhya-qv-size-toggle aria-expanded="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M7 4v6M11 4v3M15 4v6M19 4v3M4 17h16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>Size guide</button></div>
  <div class="labhya-qv-size-panel" data-labhya-qv-size-panel hidden><h3>${escapeHtml(heading)}</h3><p>${escapeHtml(note)}</p>${table}<a class="labhya-qv-size-full" href="/pages/size-chart">View full Labhya size chart →</a></div>`;
}

function render(product, productUrl) {
  activeProduct = product;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const firstAvailable = variants.find((variant) => variant.available) || variants[0];
  const description = textFromHtml(product.description || '').slice(0, 220);
  const hasVariants = variants.length > 1 || (variants[0]?.title && variants[0].title !== 'Default Title');
  const compare = Number(firstAvailable?.compare_at_price || 0);
  const price = Number(firstAvailable?.price || product.price || 0);
  const compareMarkup = compare > price ? `<s class="labhya-qv-compare">${money(compare)}</s>` : '';
  const variantOptions = variants.map((variant) => `<option value="${variant.id}" ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.title)}${variant.available ? '' : ' — Sold out'}</option>`).join('');

  content.innerHTML = `
    <div class="labhya-qv-grid">
      <div class="labhya-qv-media">${galleryMarkup(product, firstAvailable)}</div>
      <div class="labhya-qv-details">
        <span class="labhya-qv-kicker">Quick view</span>
        <h2>${escapeHtml(product.title)}</h2>
        <div class="labhya-qv-price"><span data-labhya-qv-price>${money(price)}</span>${compareMarkup}</div>
        ${description ? `<p class="labhya-qv-description">${escapeHtml(description)}${textFromHtml(product.description || '').length > 220 ? '…' : ''}</p>` : ''}
        ${hasVariants ? `<label class="labhya-qv-label" for="labhya-qv-variant">Choose option</label><select id="labhya-qv-variant" data-labhya-qv-variant>${variantOptions}</select>` : `<input type="hidden" data-labhya-qv-variant value="${firstAvailable?.id || ''}">`}
        ${sizeGuideMarkup(product)}
        <div class="labhya-qv-actions">
          <div class="labhya-qv-quantity" aria-label="Quantity">
            <button type="button" data-labhya-qv-minus aria-label="Decrease quantity">−</button>
            <input data-labhya-qv-qty type="number" value="1" min="1" inputmode="numeric" aria-label="Quantity">
            <button type="button" data-labhya-qv-plus aria-label="Increase quantity">+</button>
          </div>
          <button class="labhya-qv-add" type="button" data-labhya-qv-add ${firstAvailable?.available ? '' : 'disabled'}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3.25 4.75h2.15l2.18 10.08a2 2 0 0 0 1.96 1.58h7.28a2 2 0 0 0 1.91-1.42l1.55-5.18H7.02M9.65 20.05h.01M17.15 20.05h.01" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span>${firstAvailable?.available ? 'Add to cart' : 'Sold out'}</span>
          </button>
        </div>
        <a class="labhya-qv-full" href="${escapeHtml(productUrl)}">View full product details →</a>
        <p class="labhya-qv-status" data-labhya-qv-status aria-live="polite"></p>
      </div>
    </div>`;

  const select = content.querySelector('[data-labhya-qv-variant]');
  if (select?.tagName === 'SELECT' && firstAvailable?.id) select.value = String(firstAvailable.id);
  updateVariant();
}

function selectedVariant() {
  if (!activeProduct || !content) return null;
  const id = content.querySelector('[data-labhya-qv-variant]')?.value;
  return activeProduct.variants?.find((variant) => String(variant.id) === String(id)) || activeProduct.variants?.[0] || null;
}

function setGalleryImage(index) {
  if (!content || !activeImages.length) return;
  const total = activeImages.length;
  activeImageIndex = ((Number(index) || 0) + total) % total;
  const image = content.querySelector('[data-labhya-qv-image]');
  const count = content.querySelector('[data-labhya-qv-count]');
  if (image) image.src = activeImages[activeImageIndex];
  if (count) count.textContent = `${activeImageIndex + 1} / ${total}`;
  content.querySelectorAll('[data-labhya-qv-thumb]').forEach((thumb) => {
    thumb.setAttribute('aria-current', String(Number(thumb.dataset.labhyaQvThumb) === activeImageIndex));
  });
  content.querySelector(`[data-labhya-qv-thumb="${activeImageIndex}"]`)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
}

function updateVariant() {
  const variant = selectedVariant();
  if (!variant || !content) return;
  const image = variantImage(variant, activeProduct);
  const price = content.querySelector('[data-labhya-qv-price]');
  const add = content.querySelector('[data-labhya-qv-add]');
  if (image) {
    const index = activeImages.findIndex((src) => cleanImageKey(src) === cleanImageKey(image));
    if (index >= 0) setGalleryImage(index);
    else if (content.querySelector('[data-labhya-qv-image]')) content.querySelector('[data-labhya-qv-image]').src = image;
  }
  if (price) price.textContent = money(variant.price);
  if (add) {
    add.disabled = !variant.available;
    const label = add.querySelector('span');
    if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
  }
}

async function addSelectedToCart() {
  const variant = selectedVariant();
  if (!variant?.available || !content) return;
  const qtyInput = content.querySelector('[data-labhya-qv-qty]');
  const quantity = Math.max(1, Number(qtyInput?.value) || 1);
  const addButton = content.querySelector('[data-labhya-qv-add]');
  const status = content.querySelector('[data-labhya-qv-status]');
  const original = addButton?.querySelector('span')?.textContent || 'Add to cart';
  if (addButton) addButton.disabled = true;
  if (addButton?.querySelector('span')) addButton.querySelector('span').textContent = 'Adding…';

  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionIds = [];
  cartItemsComponents.forEach((item) => { if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId); });
  const deferred = CartLinesUpdateEvent.createPromise();
  dialog.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add', context: 'product', lines: [{ merchandiseId: String(variant.id), quantity }], promise: deferred.promise,
  }));

  try {
    const addUrl = window.Theme?.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variant.id), quantity }], sections: sectionIds.join(',') }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');

    const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartRes.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'labhya-quick-view', sourceId: 'labhya-quick-view-dialog', itemCount: quantity, productId: String(activeProduct?.id || ''), sections: data.sections, didError: false },
    });
    if (status) status.textContent = 'Added to cart.';
    if (addButton?.querySelector('span')) addButton.querySelector('span').textContent = 'Added ✓';
    setTimeout(() => { if (dialog?.open) dialog.close(); }, 500);
  } catch (err) {
    deferred.reject(err);
    if (status) status.textContent = err?.message || 'Unable to add this item.';
    if (addButton) addButton.disabled = false;
    if (addButton?.querySelector('span')) addButton.querySelector('span').textContent = original;
  }
}

async function openQuickView(button) {
  if (!dialog || !content || !loading || !errorBox) return;
  const productUrl = button.dataset.productUrl;
  if (!productUrl) return;
  activeProduct = null;
  activeImages = [];
  activeImageIndex = 0;
  loading.hidden = false;
  content.hidden = true;
  errorBox.hidden = true;
  content.innerHTML = '';
  dialog.showModal();
  document.documentElement.classList.add('labhya-qv-open');
  try {
    const res = await fetch(productJsonUrl(productUrl), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const product = await res.json();
    render(product, productUrl);
    loading.hidden = true;
    content.hidden = false;
  } catch (error) {
    console.error('Labhya Quick View failed:', error);
    loading.hidden = true;
    content.hidden = true;
    errorBox.hidden = false;
    errorBox.innerHTML = `Quick view could not be loaded. <a href="${escapeHtml(productUrl)}">Open the product page</a>.`;
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-labhya-quick-view]');
  if (trigger) { event.preventDefault(); event.stopPropagation(); openQuickView(trigger); return; }
  if (event.target.closest?.('[data-labhya-qv-close]')) { dialog?.close(); return; }
  if (event.target.closest?.('[data-labhya-qv-prev]')) { setGalleryImage(activeImageIndex - 1); return; }
  if (event.target.closest?.('[data-labhya-qv-next]')) { setGalleryImage(activeImageIndex + 1); return; }
  const thumb = event.target.closest?.('[data-labhya-qv-thumb]');
  if (thumb) { setGalleryImage(Number(thumb.dataset.labhyaQvThumb)); return; }
  const sizeToggle = event.target.closest?.('[data-labhya-qv-size-toggle]');
  if (sizeToggle) {
    const panel = content?.querySelector('[data-labhya-qv-size-panel]');
    if (panel) {
      const willOpen = panel.hidden;
      panel.hidden = !willOpen;
      sizeToggle.setAttribute('aria-expanded', String(willOpen));
    }
    return;
  }
  if (event.target.closest?.('[data-labhya-qv-minus]')) {
    const input = content?.querySelector('[data-labhya-qv-qty]'); if (input) input.value = String(Math.max(1, Number(input.value || 1) - 1)); return;
  }
  if (event.target.closest?.('[data-labhya-qv-plus]')) {
    const input = content?.querySelector('[data-labhya-qv-qty]'); if (input) input.value = String(Math.max(1, Number(input.value || 1) + 1)); return;
  }
  if (event.target.closest?.('[data-labhya-qv-add]')) { addSelectedToCart(); }
});

content?.addEventListener('change', (event) => { if (event.target.matches?.('[data-labhya-qv-variant]')) updateVariant(); });
dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
dialog?.addEventListener('close', () => {
  document.documentElement.classList.remove('labhya-qv-open');
  activeProduct = null;
  activeImages = [];
  activeImageIndex = 0;
});
